import React from 'react'
import styled from 'styled-components'

import { useMedia } from '@/utils/use-media'
import {
  AdobeIcon,
  AppleIcon,
  ArrowIcon,
  BumbleIcon,
  FacebookIcon,
  GroupmeIcon,
  InstagramIcon,
  KikaIcon,
  MailchimpIcon,
  PaypalIcon,
  PivoIcon,
  SamsungIcon,
  SignalIcon,
  SlackIcon,
  SnapIcon,
  SpotifyIcon,
  TiktokIcon,
  TwitterIcon,
  WhatsAppIcon,
} from '@/components/ui/icons'
import ShapesImage from '@/assets/images/shapes/header.svg'
import { CTA_MAP } from '@/components/common/cta'
import { desktop, mobile } from '@/utils/media-query'
import { useIsMounted } from '@/utils/use-is-mounted'

import { Marquee } from './marquee'
import { Shapes } from './shapes'
import { HeaderVideo } from './header-video'

const ShapesHeader = styled(Shapes)`
  width: 100vw;
  height: calc(100vh + 485px);
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
  margin-top: -116px;
`

const HeaderContainer = styled.div`
  @media ${desktop.query} {
    display: flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    height: calc(100vh - 116px);
    max-height: 800px;
    min-height: 625px;
    mix-blend-mode: difference;
    position: relative;
  }

  @media ${mobile.query} {
    align-items: flex-start;
    margin: auto;
    height: calc(100vh - 72px);
  }
`

const Inner = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media ${mobile.query} {
    overflow: hidden;
    width: 100vw;
    height: 100%;
  }
`

const Container = styled.div`
  @media ${desktop.query} {
    max-width: ${({ theme }) => theme.containerWidth}px;
    width: 100%;
    display: flex;
    height: 100%;
    position: relative;
    align-items: center;
  }

  @media ${mobile.query} {
    position: relative;
    width: 100vw;
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;
    z-index: 1;
    height: 100%;
  }
`

const BlurbContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - 75px);
`

const Blurb = styled.div`
  @media ${desktop.query} {
    margin-top: 100px;
    position: relative;
    z-index: 2;
    max-width: calc(100% - 463px);
    width: 100%;
    flex-shrink: 0;
    padding: 0 ${({ theme }) => theme.containerPadding}px 280px 32px;

    p {
      color: ${({ theme }) => theme.palette.giphyWhite};
      font-size: 20px;
      line-height: 30px;
      margin: 0 0 30px;
      opacity: 0.85;
      width: 75%;
    }

    h1 {
      width: 90%;
      margin-bottom: 20px;
    }
  }

  @media ${mobile.query} {
    box-sizing: border-box;
    flex-shrink: 1;
    width: 100%;
    padding: 0 15px;
    margin-top: 15%;
    margin-bottom: 15%;

    h1 {
      margin-bottom: 16px;
      line-height: 38px;
      font-size: 32px;
    }

    p {
      margin-bottom: 22px;
      max-width: 236px;
    }
  }
`

const Buttons = styled.div`
  display: flex;
  align-items: center;

  @media ${mobile.query} {
    flex-direction: column;
    align-items: flex-start;
  }
`

const MarqueeContainer = styled.div`
  @media ${desktop.query} {
    width: 100%;
    position: absolute;
    bottom: 50px;
    align-self: flex-end;
  }

  @media ${mobile.query} {
    position: relative;
    display: flex;
    flex-direction: column;
    z-index: 1;
    bottom: 30px;
  }
`

const ScrollMore = styled.div`
  align-self: center;
  transform: translateY(-50%) scale(1.5);
`

export const Header = () => {
  const isMobileScreen = useMedia(mobile.query, false)
  const isMounted = useIsMounted()
  const PrimaryCTA = CTA_MAP.default.PrimaryBodyCTA

  return (
    <section>
      <HeaderContainer>
        {isMounted && !isMobileScreen && <ShapesHeader source={ShapesImage} top="-90px" />}
        <Inner>
          {isMobileScreen
            ? isMounted && (
                <Container>
                  <HeaderVideo />
                  <BlurbContainer>
                    <Blurb>
                      <h1>GIPHY supercharges your app’s engagement</h1>
                      <p>
                        GIPHY SDK and API’s help you integrate the world&#39;s largest GIF and video
                        library, customize a suite of powerful features, and boost engagement with
                        just a few lines of code.
                      </p>
                      <Buttons>{!!PrimaryCTA && <PrimaryCTA />}</Buttons>
                    </Blurb>
                    <MarqueeContainer>
                      <Marquee>
                        <InstagramIcon small={true} />
                        <AppleIcon small={true} />
                        <SnapIcon small={true} />
                        <TwitterIcon small={true} />
                        <SamsungIcon small={true} />
                        <FacebookIcon small={true} />
                        <AdobeIcon small={true} />
                        <TiktokIcon small={true} />
                        <PaypalIcon small={true} />
                        <SlackIcon small={true} />
                        <BumbleIcon small={true} />
                        <SignalIcon small={true} />
                        <WhatsAppIcon small={true} />
                        <GroupmeIcon small={true} />
                        <MailchimpIcon small={true} />
                        <KikaIcon small={true} />
                        <PivoIcon small={true} />
                        <SpotifyIcon small={true} />
                      </Marquee>
                      <ScrollMore>
                        <ArrowIcon />
                      </ScrollMore>
                    </MarqueeContainer>
                  </BlurbContainer>
                </Container>
              )
            : isMounted && (
                <>
                  <Container>
                    <Blurb>
                      <h1>GIPHY supercharges your app’s engagement</h1>
                      <p>
                        GIPHY SDK and API’s help you integrate the world&#39;s largest GIF and video
                        library, customize a suite of powerful features, and boost engagement with
                        just a few lines of code.
                      </p>
                      <Buttons>{!!PrimaryCTA && <PrimaryCTA />}</Buttons>
                    </Blurb>
                    <HeaderVideo />
                  </Container>
                  <MarqueeContainer>
                    <Marquee>
                      <InstagramIcon small={false} />
                      <AppleIcon small={false} />
                      <SnapIcon small={false} />
                      <TwitterIcon small={false} />
                      <SamsungIcon small={false} />
                      <FacebookIcon small={false} />
                      <AdobeIcon small={false} />
                      <TiktokIcon small={false} />
                      <PaypalIcon small={false} />
                      <SlackIcon small={false} />
                      <BumbleIcon small={false} />
                      <SignalIcon small={false} />
                      <WhatsAppIcon small={false} />
                      <GroupmeIcon small={false} />
                      <MailchimpIcon small={false} />
                      <KikaIcon small={false} />
                      <PivoIcon small={false} />
                      <SpotifyIcon small={false} />
                    </Marquee>
                  </MarqueeContainer>
                </>
              )}
        </Inner>
      </HeaderContainer>
    </section>
  )
}
